<template>
  <section class="skills-section enhanced-section">
    <div class="section-container">
      <div class="section-header">
        <h2 class="section-title enhanced-title">Skills & Expertise</h2>
        <p class="section-description">{{ description }}</p>
      </div>

      <v-row class="skills-grid">
        <v-col
          cols="12"
          sm="6"
          md="4"
          lg="3"
          v-for="skill in skills"
          :key="skill.title"
        >
          <v-card
            class="skill-card modern-skill-card"
            :class="{ 'is-hovered': skill.isHovered }"
            @mouseenter="handleCardHover(skill, true)"
            @mouseleave="handleCardHover(skill, false)"
            elevation="0"
          >
            <!-- Enhanced Skill Header -->
            <div class="skill-header modern-header">
              <div class="skill-icon-wrapper">
                <v-avatar
                  class="skill-icon modern-icon"
                  size="64"
                  :style="getIconStyle(skill.color)"
                >
                  <v-icon
                    :color="skill.color"
                    size="32"
                    class="skill-icon-svg"
                  >
                    {{ skill.icon }}
                  </v-icon>
                </v-avatar>
                <div class="icon-glow" :style="getGlowStyle(skill.color)"></div>
              </div>

              <h3 class="skill-name modern-name" :style="getSkillNameStyle(skill.color)">
                {{ skill.title }}
              </h3>
            </div>

            <!-- Enhanced Skill Content -->
            <v-card-text class="skill-content modern-content">
              <div class="progress-wrapper modern-progress">
                <div class="progress-label enhanced-label">
                  <span class="level-label">{{ getLevelLabel(skill.level) }}</span>
                  <span
                    class="level-percentage enhanced-percentage"
                    :style="getPercentageStyle(skill.color)"
                  >
                    {{ skill.displayLevel || 0 }}%
                  </span>
                </div>

                <!-- Modern Progress Bar -->
                <div class="progress-container modern-progress-container">
                  <div class="progress-track"></div>
                  <div
                    class="progress-bar modern-progress-bar"
                    :style="getProgressBarStyle(skill.displayLevel || 0, skill.color)"
                    :class="{ 'animate-progress': isAnimating }"
                  ></div>
                  <div class="progress-shine" :style="getShineStyle(skill.color)"></div>
                </div>
              </div>

              <p class="skill-description modern-description">
                {{ skill.description }}
              </p>

              <!-- Skill Level Badge -->
              <div class="skill-level-badge" :style="getBadgeStyle(skill.color, skill.level)">
                <v-icon small class="badge-icon">{{ getLevelIcon(skill.level) }}</v-icon>
                <span class="badge-text">{{ getLevelLabel(skill.level) }}</span>
              </div>
            </v-card-text>

            <!-- Card Overlay Effect -->
            <div class="card-overlay" :style="getOverlayStyle(skill.color)"></div>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </section>
</template>

<script>
export default {
  name: "SkillsSection",
  props: {
    skills: {
      type: Array,
      required: true,
      default: () => []
    },
    description: {
      type: String,
      default: "My technical expertise and proficiency levels"
    }
  },
  data() {
    return {
      isAnimating: false,
      animationTimeout: null
    };
  },
  methods: {
    handleCardHover(skill, isHovered) {
      this.$set(skill, 'isHovered', isHovered);

      if (isHovered && !this.isAnimating) {
        this.animateProgressBars();
      }
    },

    animateProgressBars() {
      this.isAnimating = true;

      // Animate each skill's progress
      this.skills.forEach((skill, index) => {
        setTimeout(() => {
          this.animateSkillProgress(skill);
        }, index * 100);
      });

      // Reset animation state
      this.animationTimeout = setTimeout(() => {
        this.isAnimating = false;
      }, 2000);
    },

    animateSkillProgress(skill) {
      let currentLevel = 0;
      const targetLevel = skill.level;
      const increment = targetLevel / 30; // 30 frames for smooth animation

      const animate = () => {
        if (currentLevel < targetLevel) {
          currentLevel = Math.min(currentLevel + increment, targetLevel);
          this.$set(skill, 'displayLevel', Math.round(currentLevel));
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    },

    getLevelLabel(level) {
      if (level >= 90) return 'Expert';
      if (level >= 80) return 'Advanced';
      if (level >= 70) return 'Proficient';
      if (level >= 60) return 'Intermediate';
      return 'Beginner';
    },

    getLevelIcon(level) {
      if (level >= 90) return 'mdi-star';
      if (level >= 80) return 'mdi-medal';
      if (level >= 70) return 'mdi-trophy-variant';
      if (level >= 60) return 'mdi-check-circle';
      return 'mdi-circle-outline';
    },

    getIconStyle(color) {
      return {
        background: `linear-gradient(135deg, ${color}20, ${color}10)`,
        border: `2px solid ${color}30`,
        backdropFilter: 'blur(10px)',
        transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)'
      };
    },

    getGlowStyle(color) {
      return {
        background: `radial-gradient(circle, ${color}40, transparent 70%)`,
        filter: 'blur(20px)',
        opacity: '0'
      };
    },

    getSkillNameStyle(color) {
      return {
        background: `linear-gradient(135deg, ${color}, ${color}CC)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        filter: `drop-shadow(0 2px 4px ${color}30)`
      };
    },

    getPercentageStyle(color) {
      return {
        color: color,
        textShadow: `0 0 10px ${color}50`
      };
    },

    getProgressBarStyle(level, color) {
      return {
        width: `${level}%`,
        background: `linear-gradient(90deg, ${color}, ${color}DD, ${color}AA)`,
        boxShadow: `0 0 20px ${color}60, inset 0 1px 0 rgba(255,255,255,0.2)`,
        position: 'relative'
      };
    },

    getShineStyle(color) {
      return {
        background: `linear-gradient(90deg, transparent, ${color}80, transparent)`,
        animation: 'progressShine 2s ease-in-out infinite'
      };
    },

    getBadgeStyle(color, level) {
      const opacity = level >= 80 ? '1' : '0.7';
      return {
        background: `linear-gradient(135deg, ${color}20, ${color}10)`,
        border: `1px solid ${color}40`,
        color: color,
        opacity: opacity
      };
    },

    getOverlayStyle(color) {
      return {
        background: `linear-gradient(135deg, ${color}05, transparent, ${color}05)`,
        opacity: '0'
      };
    }
  },

  mounted() {
    // Initialize display levels
    this.skills.forEach(skill => {
      this.$set(skill, 'displayLevel', 0);
      this.$set(skill, 'isHovered', false);
    });

    // Start animation after component is mounted
    setTimeout(() => {
      this.animateProgressBars();
    }, 500);
  },

  beforeDestroy() {
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.skills-section {
  padding: 4rem 0;
  position: relative;
  overflow: hidden;
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, $primary, $accent);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 4px 20px rgba(67, 97, 238, 0.3);

  .theme--light & {
    text-shadow: 0 2px 10px rgba(67, 97, 238, 0.2);
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 2.2rem;
  }
}

.section-description {
  max-width: 600px;
  margin: 0 auto;
  font-size: 1.2rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.skills-grid {
  margin-top: 2rem;
}

// Modern Skill Card
.modern-skill-card {
  height: 100%;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  background: rgba(255, 255, 255, 0.08) !important;
  backdrop-filter: blur(25px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  cursor: pointer;

  .theme--light & {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(67, 97, 238, 0.08);
    box-shadow: 0 8px 32px rgba(67, 97, 238, 0.08);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, transparent, rgba(255,255,255,0.05), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &.is-hovered {
    transform: translateY(-15px) scale(1.02);
    border-color: rgba(67, 97, 238, 0.4);
    box-shadow:
      0 25px 50px rgba(0, 0, 0, 0.4),
      0 0 40px rgba(67, 97, 238, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);

    .theme--light & {
      box-shadow:
        0 25px 50px rgba(67, 97, 238, 0.15),
        0 0 40px rgba(67, 97, 238, 0.1);
      border-color: rgba(67, 97, 238, 0.2);
    }

    &::before {
      opacity: 1;
    }

    .card-overlay {
      opacity: 1 !important;
    }

    .icon-glow {
      opacity: 0.6 !important;
      transform: scale(1.5);
    }

    .skill-icon {
      transform: scale(1.15) rotate(5deg);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    }

    .progress-shine {
      animation: progressShine 1.5s ease-in-out;
    }
  }
}

// Modern Header
.modern-header {
  padding: 2rem 1.5rem 1.5rem;
  text-align: center;
  position: relative;
  background: linear-gradient(135deg, rgba(255,255,255,0.03), transparent);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  .theme--light & {
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.02), transparent);
    border-bottom: 1px solid rgba(67, 97, 238, 0.05);
  }
}

.skill-icon-wrapper {
  position: relative;
  display: inline-block;
  margin-bottom: 1rem;

  .icon-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120px;
    height: 120px;
    transition: all 0.4s ease;
    z-index: 0;
  }

  .skill-icon {
    position: relative;
    z-index: 1;
    transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  }
}

.modern-name {
  font-weight: 700;
  font-size: 1.3rem;
  text-align: center;
  margin: 0;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0.5px;
}

// Modern Content
.modern-content {
  padding: 1.5rem 2rem 2rem;
  position: relative;
}

.modern-progress {
  margin-bottom: 1.5rem;
}

.enhanced-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.level-label {
  font-weight: 600;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;

  .theme--light & {
    color: rgba(73, 80, 87, 0.9);
  }
}

.enhanced-percentage {
  font-weight: 800;
  font-size: 1.1rem;
  font-family: 'Poppins', sans-serif;
}

// Modern Progress Container
.modern-progress-container {
  height: 12px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);

  .theme--light & {
    background: rgba(67, 97, 238, 0.06);
    border-color: rgba(67, 97, 238, 0.1);
  }
}

.progress-track {
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
    rgba(255,255,255,0.02),
    rgba(255,255,255,0.05),
    rgba(255,255,255,0.02)
  );
}

.modern-progress-bar {
  height: 100%;
  border-radius: 8px;
  position: relative;
  transition: all 1.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg,
      rgba(255,255,255,0),
      rgba(255,255,255,0.3),
      rgba(255,255,255,0)
    );
    transform: translateX(-100%);
  }

  &.animate-progress::after {
    animation: progressSlide 1.5s ease-out 0.5s;
  }
}

.progress-shine {
  position: absolute;
  top: 0;
  left: 0;
  width: 30%;
  height: 100%;
  opacity: 0;
}

.modern-description {
  font-size: 0.95rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1.5rem;

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.skill-level-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  .badge-icon {
    opacity: 0.8;
  }
}

.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  transition: opacity 0.3s ease;
  z-index: 0;
}

// Animations
@keyframes progressShine {
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
}

@keyframes progressSlide {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

// Responsive Design
@media (max-width: $breakpoint-md) {
  .section-title {
    font-size: 2.5rem;
  }

  .modern-header {
    padding: 1.5rem 1rem;
  }

  .modern-content {
    padding: 1rem 1.5rem 1.5rem;
  }
}

@media (max-width: $breakpoint-xs) {
  .skills-section {
    padding: 2rem 0;
  }

  .section-container {
    padding: 0 1rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .section-description {
    font-size: 1rem;
  }

  .modern-skill-card {
    border-radius: 16px;

    &.is-hovered {
      transform: translateY(-8px) scale(1.01);
    }
  }

  .modern-header {
    padding: 1.5rem 1rem 1rem;
  }

  .skill-icon {
    size: 56px;

    .v-icon {
      font-size: 28px;
    }
  }

  .modern-name {
    font-size: 1.1rem;
  }

  .modern-content {
    padding: 1rem 1.2rem 1.5rem;
  }
}
</style>